import { FC, useCallback, useEffect, useState } from "react";
import { CDialog } from "../Dialogs/CDialog";
import { performEnterpriseGPTApiCall, useEnterpriseGPTApiCall } from "../../Hooks/useMiddlewareApiCall";
import { ISurveyAnswer, SurveyResponse } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { useNotification } from "../../Contexts/NotificationContext";
import { useConversations, useSurvey } from "../../store/hooks";
import moment from "moment";
import SurveyView from "./SurveyView";
import { resetSurveyAction, setSurveyAction } from "../../store/actions/surveyAction";
import { appInsights } from "../../AppInsights";
import { useUserContext } from "../../Contexts/UserContext";

const SurveyHandler: FC = () => {
    const { chats } = useConversations();
    const { dispatchNotification } = useNotification();
    const [showSurvey, setShowSurvey] = useState<boolean>(false);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState<boolean>(false);
    const { survey, dispatch } = useSurvey();   // get the latest survey from the store, if available
    const [answersDict, setAnswersDict] = useState<{ [key: string]: ISurveyAnswer }>({});
    const { userProfile } = useUserContext();

    // Fetch the active survey only after this component mounts, which is guaranteed to happen
    // after enrollment is fully confirmed (SurveyHandler lives inside EnrollmentChecker's children).
    // This avoids a race condition where getActiveSurveyForUser() was previously called in Router
    // in parallel with the enrollment flow, causing it to return null for new/re-enrolling users.
    useEnterpriseGPTApiCall(async (client, abortSignal) => {
        try {
            const activeSurvey = await client.getActiveSurveyForUser(abortSignal);
            const hasSurvey = !!activeSurvey?.id;
            console.log('[SurveyHandler] getActiveSurveyForUser result:', hasSurvey ? `survey found (id=${activeSurvey.id}, title="${activeSurvey.title}")` : 'no active survey for user (null/undefined)');
            appInsights.trackTrace({
                message: `[SurveyHandler] getActiveSurveyForUser result; user: ${userProfile?.username ?? 'unknown'}`,
                properties: {
                    hasSurvey,
                    surveyId: activeSurvey?.id ?? null,
                    surveyTitle: activeSurvey?.title ?? null,
                    surveyStatus: activeSurvey?.status ?? null,
                },
            });
            // Only dispatch a survey that has a real id. processGetActiveSurveyForUser can return an empty
            // Survey object {} (truthy but id-less) when the server returns 204/null — dispatching that
            // would put a truthy value in the store and inadvertently block both the survey dialog and
            // any feature highlight popups.
            dispatch(setSurveyAction(hasSurvey ? activeSurvey : null as any));
        } catch (e) {
            if ((e as Error).message !== 'canceled') {
                console.warn('[SurveyHandler] getActiveSurveyForUser error:', e);
                appInsights.trackTrace({
                    message: '[SurveyHandler] getActiveSurveyForUser error',
                    properties: { error: String(e) },
                });
                dispatchNotification('error.api.get.survey', 'error');
            }
        }
    });

    // Show the survey if there is at least one non-draft chat that started before today.
    useEffect(() => {
        if (survey?.id) {
            const qualifyingChats = chats.filter(c => c.draft === false && moment(c.startedOn).isBefore(moment().startOf('day')));
            const shouldShow = qualifyingChats.length > 0;
            console.log(`[SurveyHandler] survey eligibility check — surveyId=${survey.id}, totalChats=${chats.length}, qualifyingChats(non-draft before today)=${qualifyingChats.length} → showSurvey=${shouldShow}`);
            setShowSurvey(shouldShow);
        } else {
            console.log(`[SurveyHandler] survey eligibility check skipped — no active survey in store`);
        }
    }, [survey, chats]);

    // Enable the submit button if all mandatory questions have been answered.
    useEffect(() => {
        const isValid = survey?.questions?.every(q => !q.isMandatory || (q.id! in answersDict && !!answersDict[q.id!].value)) ?? false;
        setIsSubmitEnabled(isValid);
    }, [survey, answersDict]);

    // Log survey model when it changes
    useEffect(() => {
        if (!survey?.id) 
            return;
        console.log('[SurveyHandler] survey loaded into store:', {
            id: survey.id,
            title: survey.title,
            status: survey.status,
            questions: (survey.questions ?? []).map(q => ({
                id: q.id,
                isMandatory: q.isMandatory,
                type: q.type,
                text: q.text,
            })),
        });
    }, [survey]);

    // Log answer state and submit validity whenever answers change
    useEffect(() => {
        if (!survey?.id) 
            return;
        const diagnostics = (survey.questions ?? []).map(q => {
            const key = String(q.id ?? '');
            const answer = answersDict[key];
            const rawVal = answer?.value;
            const strVal = rawVal == null ? rawVal : String(rawVal);
            const trimmed = typeof strVal === 'string' ? strVal.trim() : strVal;
            const existsInDict = key in answersDict;
            const passesValueCheck = !(trimmed == null) && (typeof trimmed !== 'string' || trimmed.length > 0) && !(trimmed === '0' && q.type === 'Rating');
            const passes = !q.isMandatory || (existsInDict && passesValueCheck);
            return { id: key, isMandatory: q.isMandatory, type: q.type, rawVal, trimmed, existsInDict, passes };
        });
        const allPass = diagnostics.every(d => d.passes);
        console.log(`[SurveyHandler] answer validation — isSubmitEnabled=${allPass}`, diagnostics);
    }, [survey, answersDict, isSubmitEnabled]);

    const onAnswerChange = (answer: ISurveyAnswer) => {
        setAnswersDict(a => ({
            ...a,
            [answer.id!]: answer
        }));
    };

    const onSubmitSurvey = useCallback(() => {
        // Close immediately so Edge (and all browsers) don't leave the dialog open while the API call is in flight,
        // which was causing click events on the Submit button to be swallowed in Edge (same issue as FeatureHighlightPopup).
        console.log(`[SurveyHandler] Submit clicked — surveyId=${survey?.id}, answers=${Object.keys(answersDict).length} — closing dialog immediately, submitting in background`);
        setShowSurvey(false);
        dispatch(resetSurveyAction());
        performEnterpriseGPTApiCall(async (client, cancelToken) => {
            try {
                const surveyResponse: SurveyResponse = new SurveyResponse({
                    surveyId: survey?.id,
                    answers: Object.values(answersDict).sort((a, b) => parseInt(a.id!) - parseInt(b.id!))
                });

                await client.submitSurveyResponse(surveyResponse, cancelToken);
                console.log(`[SurveyHandler] survey response submitted successfully for surveyId=${survey?.id}`);
            } catch (e) {
                console.warn(`[SurveyHandler] survey response submission failed for surveyId=${survey?.id}:`, e);
                dispatchNotification('error.api.post.conversation', 'error');
            }
        });
    }, [survey, answersDict]);

    // display the survey dialog
    return <CDialog open={showSurvey} onPrimaryAction={onSubmitSurvey} primaryActionText="Submit" disablePrimaryAction={!isSubmitEnabled}>
        <SurveyView surveyModel={survey} onAnswerChange={onAnswerChange} />
    </CDialog>
};

export default SurveyHandler;